// ============================================================
// src/pages/AllTimesheetsPage.jsx  [Supervisor]
// No extra packages needed — custom calendar built from scratch
// ============================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAllTimesheets, getKPIs, getWeekEntries, reviewTimesheet, addManualEntry, exportTimesheets, supervisorUpdateEntry } from '../api/timesheetApi';
import { getEmployees } from '../api/employeeApi';
import { Toast, Btn, Field, Input, Select, Modal, StatusBadge, Spinner } from '../components/UI';
import { useToast } from '../hooks/useToast';
import { getCurrentWeekEnding, formatFromDate, parseDate, formatTime } from '../utils/dateHelper';

function getInitialManualForm() {
  return {
    employee_id: '',
    entry_date: getCurrentWeekEnding(),
    work_order: '',
    job_number: '',
    location: 'Shop',   // ← ADDED
    description: '',
    time_in: '07:00',
    time_out: '15:30',
    hours_worked: '',
  };
}

// ── Calendar constants ───────────────────────────────────────
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// ── Custom DatePicker (zero deps, position:fixed popup) ──────
function DatePicker({ value, onChange, small = false }) {
  const selected = parseDate(value);
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState((selected || today).getFullYear());
  const [viewMonth, setViewMonth] = useState((selected || today).getMonth());
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  useEffect(() => {
    if (selected) { setViewYear(selected.getFullYear()); setViewMonth(selected.getMonth()); }
  }, [value]);

  const prevMonth = () => viewMonth === 0 ? (setViewMonth(11), setViewYear(y => y - 1)) : setViewMonth(m => m - 1);
  const nextMonth = () => viewMonth === 11 ? (setViewMonth(0), setViewYear(y => y + 1)) : setViewMonth(m => m + 1);

  function buildDays() {
    const first = new Date(viewYear, viewMonth, 1).getDay();
    const total = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);
    return cells;
  }

  const isSelected = d => d && selected &&
    selected.getFullYear() === viewYear && selected.getMonth() === viewMonth && selected.getDate() === d;
  const isToday = d => d &&
    today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === d;

  function selectDay(day) {
    if (!day) return;
    onChange(formatFromDate(new Date(viewYear, viewMonth, day)));
    setOpen(false);
  }

  const years = [];
  for (let y = today.getFullYear() - 5; y <= today.getFullYear() + 5; y++) years.push(y);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative', width: '100%' }}>
        <input
          type="text" readOnly
          value={value || ''}
          placeholder="yyyy-mm-dd"
          onClick={() => setOpen(o => !o)}
          style={{
            width: '100%', boxSizing: 'border-box',
            border: `1.5px solid ${open ? '#dc2626' : '#d1d5db'}`,
            borderRadius: '6px',
            padding: small ? '0.28rem 2.2rem 0.28rem 0.55rem' : '0.48rem 2.4rem 0.48rem 0.75rem',
            fontSize: small ? '0.8rem' : '0.875rem',
            color: value ? '#111827' : '#9ca3af',
            background: '#fff', outline: 'none', cursor: 'pointer',
            transition: 'border-color 0.15s',
          }}
        />
        <span onClick={() => setOpen(o => !o)} style={{
          position: 'absolute', right: '0.55rem', top: '50%',
          transform: 'translateY(-50%)',
          fontSize: small ? '0.9rem' : '1rem',
          cursor: 'pointer', userSelect: 'none', lineHeight: 1,
        }}>📅</span>
      </div>

      {open && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0,
            zIndex: 99999,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            width: '288px',
            userSelect: 'none',
          }}
          ref={el => {
            if (!el || !wrapRef.current) return;
            const rect = wrapRef.current.getBoundingClientRect();
            const popH = el.offsetHeight || 300;
            const below = window.innerHeight - rect.bottom;
            el.style.top = below >= popH + 8 || below >= rect.top
              ? `${rect.bottom + 6}px`
              : `${rect.top - popH - 6}px`;
            let left = rect.left;
            if (left + 288 > window.innerWidth - 8) left = window.innerWidth - 296;
            if (left < 8) left = 8;
            el.style.left = `${left}px`;
          }}
        >
          <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <button onClick={prevMonth} style={navBtn}>‹</button>
              <div style={{ display: 'flex', gap: '6px' }}>
                <select value={viewMonth} onChange={e => setViewMonth(+e.target.value)} style={dropStyle}>
                  {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                </select>
                <select value={viewYear} onChange={e => setViewYear(+e.target.value)} style={dropStyle}>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <button onClick={nextMonth} style={navBtn}>›</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
              {DAYS.map(d => (
                <div key={d} style={{ textAlign: 'center', fontSize: '0.62rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', lineHeight: '26px' }}>{d}</div>
              ))}
            </div>
          </div>

          <div style={{ padding: '8px 10px 12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
              {buildDays().map((day, i) => (
                <div key={i} onClick={() => selectDay(day)} style={{
                  height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '7px', fontSize: '0.82rem',
                  cursor: day ? 'pointer' : 'default',
                  fontWeight: isSelected(day) || isToday(day) ? 700 : 400,
                  background: isSelected(day) ? '#dc2626' : 'transparent',
                  color: isSelected(day) ? '#fff' : isToday(day) ? '#dc2626' : day ? '#374151' : 'transparent',
                  transition: 'background 0.1s, color 0.1s',
                }}
                  onMouseEnter={e => { if (day && !isSelected(day)) { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; } }}
                  onMouseLeave={e => { if (day && !isSelected(day)) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isToday(day) ? '#dc2626' : '#374151'; } }}
                >{day || ''}</div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: '8px', borderTop: '1px solid #f3f4f6', paddingTop: '8px' }}>
              <button onClick={() => { onChange(formatFromDate(today)); setOpen(false); }} style={{
                background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px',
                padding: '3px 14px', fontSize: '0.75rem', fontWeight: 600,
                color: '#374151', cursor: 'pointer',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#dc2626'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
              >Today</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const navBtn = { background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', color: '#6b7280', lineHeight: 1 };
const dropStyle = { fontSize: '0.78rem', fontWeight: 600, border: '1px solid #e5e7eb', borderRadius: '5px', padding: '3px 6px', color: '#374151', background: '#f9fafb', outline: 'none', cursor: 'pointer' };

// ── Location Badge helper ────────────────────────────────────
function LocationBadge({ location }) {
  const isOffSite = location === 'Off-Site';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
      fontSize: '0.72rem', fontWeight: 600,
      background: isOffSite ? '#fef3c7' : '#dcfce7',
      color: isOffSite ? '#b45309' : '#15803d',
      whiteSpace: 'nowrap',
    }}>
      {location || 'Shop'}
    </span>
  );
}

// ── Location Select helper ───────────────────────────────────
function LocationSelect({ value, onChange, style = {} }) {
  return (
    <select
      value={value || 'Shop'}
      onChange={onChange}
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: '0.45rem 0.65rem',
        border: '1px solid #d1d5db', borderRadius: '4px',
        fontSize: '0.875rem', color: '#111827',
        background: '#fff', outline: 'none',
        fontFamily: 'inherit', cursor: 'pointer',
        ...style,
      }}
    >
      <option value="Shop">Shop</option>
      <option value="Off-Site">Off-Site</option>
    </select>
  );
}

// ── Stat Card ────────────────────────────────────────────────
function StatCard({ label, value, sub, color = 'gray' }) {
  const borders = { gray: '#9ca3af', green: '#16a34a', yellow: '#d97706', red: '#dc2626' };
  const values  = { gray: '#111827', green: '#16a34a', yellow: '#d97706', red: '#dc2626' };
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb',
      borderTop: `3px solid ${borders[color]}`,
      borderRadius: '6px', padding: '1rem 1.25rem',
      boxShadow: '0 1px 3px rgba(0,0,0,.06)', minWidth: '140px',
    }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '.06em', color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.35rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </div>
      <div style={{ fontSize: '2rem', fontWeight: 700, color: values[color], lineHeight: 1.1, marginBottom: '0.25rem' }}>
        {value}
      </div>
      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{sub}</div>
    </div>
  );
}

// ── Wide Modal (Detail page only) ────────────────────────────
function WideModal({ open, title, onClose, children, footer }) {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#fff', borderRadius: '8px',
        width: '95vw', maxWidth: '95vw', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }} role="dialog">
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
          {children}
        </div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
export default function AllTimesheetsPage() {
  const { toast, showToast, clearToast } = useToast();
  const [rows, setRows]       = useState([]);
  const [kpis, setKpis]       = useState({});
  const [loading, setLoading] = useState(true);
  const [mechanics, setMechanics] = useState([]);
  const [filters, setFilters] = useState({
    week_ending: getCurrentWeekEnding(), status: '', search: '',
  });

  // ── Edit entry states ──
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm]         = useState({});
  const [savingEdit, setSavingEdit]     = useState(false);
  const eUpd = k => e => setEditForm(f => ({ ...f, [k]: e.target.value }));

  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewAction, setReviewAction] = useState('APPROVE');
  const [reviewNotes, setReviewNotes]   = useState('');
  const [reviewing, setReviewing]       = useState(false);
  const [wideModalNote, setWideModalNote] = useState('');
  const [detailWeek, setDetailWeek]       = useState(null);
  const [detailEntries, setDetailEntries] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 600);
  React.useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const [manualOpen, setManualOpen]     = useState(false);
  const [manualForm, setManualForm]     = useState(getInitialManualForm);
  const [savingManual, setSavingManual] = useState(false);
  const closeManualModal = () => { setManualOpen(false); setManualForm(getInitialManualForm()); };
  const mUpd = k => e => setManualForm(f => ({ ...f, [k]: e.target.value }));

  const toHourMinuteString = totalMinutes => {
    const hours   = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}.${String(minutes).padStart(2, '0')}`;
  };

  const toMinutes = value => {
    if (value === null || value === undefined || value === '') return 0;
    const [hPart, mPart = '00'] = String(value).split('.');
    const h = Number(hPart);
    const m = Number((mPart || '00').padEnd(2, '0').slice(0, 2));
    if (Number.isNaN(h) || Number.isNaN(m)) return 0;
    return (h * 60) + Math.min(Math.max(m, 0), 59);
  };

  // ── Auto-calc hours for edit form ──
  useEffect(() => {
    if (!editForm.time_in || !editForm.time_out) return;
    const [h1, m1] = editForm.time_in.split(':').map(Number);
    const [h2, m2] = editForm.time_out.split(':').map(Number);
    if (isNaN(h1) || isNaN(h2)) return;
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (mins < 0) mins += 1440;
    setEditForm(f => ({ ...f, hours_worked: mins > 0 ? toHourMinuteString(mins) : '' }));
  }, [editForm.time_in, editForm.time_out]);

  // ── Save edit handler ──
  async function handleSaveEdit() {
    if (!editForm.job_number?.trim() || !editForm.description?.trim()) {
      showToast('Job # and Description required', 'error'); return;
    }
    setSavingEdit(true);
    try {
      await supervisorUpdateEntry(editingEntry.id, {
        entry_date:   editForm.entry_date,
        work_order:   editForm.work_order,
        job_number:   editForm.job_number,
        location:     editForm.location || 'Shop',   // ← ADDED
        description:  editForm.description,
        time_in:      editForm.time_in,
        time_out:     editForm.time_out,
        hours_worked: editForm.hours_worked,
      });
      showToast('Entry updated ✓', 'success');
      setEditingEntry(null);
      const updated = await getWeekEntries(detailWeek.id);
      setDetailEntries(updated);
      fetchData();
    } catch (err) {
      showToast(err.message || 'Failed to update', 'error');
    } finally {
      setSavingEdit(false);
    }
  }

  // ── Auto-calc hours for manual form ──
  useEffect(() => {
    const [h1, m1] = manualForm.time_in.split(':').map(Number);
    const [h2, m2] = manualForm.time_out.split(':').map(Number);
    if (isNaN(h1) || isNaN(h2)) return;
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (mins < 0) mins += 1440;
    setManualForm(f => ({ ...f, hours_worked: mins > 0 ? toHourMinuteString(mins) : '' }));
  }, [manualForm.time_in, manualForm.time_out]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tsData, kpiData] = await Promise.all([
        getAllTimesheets(filters),
        getKPIs(filters.week_ending),
      ]);
      setRows(tsData);
      setKpis(kpiData);
    } catch (err) {
      showToast(err.message || 'Failed to load', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { getEmployees().then(setMechanics).catch(() => {}); }, []);

  // ── KPI derived ──
  const totalMechanics    = Number(kpis.total_mechanics ?? 0);
  const approved          = Number(kpis.approved ?? 0);
  const notSubmitted      = Number(kpis.not_submitted ?? 0);
  const totalHoursMins    = toMinutes(kpis.total_hours || 0);
  const totalOvertimeMins = toMinutes(kpis.total_overtime || 0);
  const totalHours        = toHourMinuteString(totalHoursMins);
  const totalOvertime     = toHourMinuteString(totalOvertimeMins);
  const pendingReview     = Number(kpis.pending_review ?? 0);
  const completionPct     = totalMechanics > 0 ? Math.round((approved / totalMechanics) * 100) : 0;
  const avgHrs            = totalMechanics > 0 ? toHourMinuteString(Math.round(totalHoursMins / totalMechanics)) : '0.00';
  const otPct             = totalHoursMins > 0 ? ((totalOvertimeMins / totalHoursMins) * 100).toFixed(1) : '0.0';

  async function handleExport() {
    try {
      await exportTimesheets(filters.week_ending, filters.status, filters.search);
      showToast('Excel downloaded ✓', 'success');
    } catch (err) {
      showToast(err.message || 'Export failed', 'error');
    }
  }

  async function handleReview() {
    setReviewing(true);
    try {
      await reviewTimesheet(reviewTarget.id, reviewAction, reviewNotes);
      showToast(`Timesheet ${reviewAction.toLowerCase()}d ✓`, 'success');
      setReviewTarget(null); setReviewNotes('');
      fetchData();
    } catch (err) {
      showToast(err.message || 'Review failed', 'error');
    } finally {
      setReviewing(false);
    }
  }

  async function openDetail(week) {
    setDetailWeek(week);
    setLoadingDetail(true);
    try { setDetailEntries(await getWeekEntries(week.id)); }
    catch { setDetailEntries([]); }
    finally { setLoadingDetail(false); }
  }

  async function handleManualEntry(e) {
    e.preventDefault();
    if (!manualForm.employee_id || !manualForm.job_number.trim() || !manualForm.description.trim()) {
      showToast('Employee, Job # and Description required', 'error'); return;
    }
    setSavingManual(true);
    try {
      await addManualEntry({
        employee_id:  manualForm.employee_id,
        entry_date:   manualForm.entry_date,
        work_order:   manualForm.work_order.trim(),
        job_number:   manualForm.job_number.trim(),
        location:     manualForm.location || 'Shop',   // ← ADDED
        description:  manualForm.description.trim(),
        time_in:      manualForm.time_in,
        time_out:     manualForm.time_out,
        hours_worked: manualForm.hours_worked,
      });
      showToast('Manual entry added ✓', 'success');
      closeManualModal();
      fetchData();
    } catch (err) {
      showToast(err.message || 'Failed to add', 'error');
    } finally {
      setSavingManual(false);
    }
  }

  // ── Helper: open edit with location pre-populated ──
  function openEditEntry(en) {
    setEditingEntry(en);
    setEditForm({
      entry_date:   en.entry_date,
      work_order:   en.work_order || '',
      job_number:   en.job_number || '',
      location:     en.location || 'Shop',   // ← ADDED
      description:  en.description || '',
      time_in:      en.time_in?.slice(0, 5) || '',
      time_out:     en.time_out?.slice(0, 5) || '',
      hours_worked: toHourMinuteString(toMinutes(en.hours_worked)),
    });
  }

  const fUpd = k => e => setFilters(f => ({ ...f, [k]: e.target.value }));

  const inpStyle = {
    border: '1px solid #d1d5db', borderRadius: '4px',
    padding: '0.45rem 0.65rem', fontSize: '0.875rem',
    outline: 'none', background: '#fff', color: '#111827',
    width: '100%', boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: '1.5rem', background: '#f3f4f6', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, margin: 0, letterSpacing: '.02em', color: '#111827' }}>
            ALL TIMESHEETS
          </h1>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
            Real-time view — Week Ending{' '}
            <span style={{ color: '#dc2626', fontWeight: 600 }}>{filters.week_ending || '—'}</span>
          </p>
        </div>
        <button onClick={handleExport} style={{
          background: '#dc2626', color: '#fff', border: 'none',
          padding: '0.6rem 1.25rem', borderRadius: '5px',
          fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          boxShadow: '0 1px 3px rgba(0,0,0,.15)',
        }}>⬇ EXPORT EXCEL</button>
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <StatCard label="Total Mechanics"    value={totalMechanics} sub="Active this week"      color="gray" />
        <StatCard label="Approved"           value={approved}       sub={`${completionPct}% completion`} color="green" />
        <StatCard label="Pending Review"     value={pendingReview}  sub="Awaiting approval"     color="yellow" />
        <StatCard label="Not Submitted"      value={notSubmitted}   sub="Follow-up required"    color="red" />
        <StatCard label="Total Hours (Week)" value={totalHours}     sub={`Avg ${avgHrs} hrs/person`} color="gray" />
        <StatCard label="Overtime Hours"     value={totalOvertime}  sub={`${otPct}% of total`}  color={totalOvertimeMins > 0 ? 'red' : 'gray'} />
      </div>

      {/* ── Filter Row ── */}
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px',
        padding: '1rem 1.25rem', marginBottom: '1.25rem',
        display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: '180px' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '0.3rem' }}>Search</div>
          <input type="text" style={inpStyle} value={filters.search} onChange={fUpd('search')}
            placeholder="Name or EE#…" onKeyDown={e => e.key === 'Enter' && fetchData()} />
        </div>
        <div style={{ minWidth: '140px' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '0.3rem' }}>Status</div>
          <select style={{ ...inpStyle, width: 'auto', minWidth: '140px' }} value={filters.status} onChange={fUpd('status')}>
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div style={{ minWidth: '160px' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '0.3rem' }}>Week Ending</div>
          <DatePicker
            value={filters.week_ending}
            onChange={val => setFilters(f => ({ ...f, week_ending: val }))}
          />
        </div>
        <button onClick={() => setManualOpen(true)} style={{
          background: '#dc2626', color: '#fff', border: 'none',
          padding: '0.5rem 1rem', borderRadius: '4px',
          fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap',
        }}>+ ADD ENTRY</button>
      </div>

      {/* ── Main Table ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
          Timesheet Records
        </div>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}><Spinner /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['EE#', 'Last Name', 'First Name', 'Regular Hrs', 'OT Hrs', 'Total', 'Entries', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '0.65rem 1rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>No timesheets found.</td></tr>
                ) : rows.map(r => (
                  <tr key={`${r.employee_id}-${r.id || 'none'}`}
                    style={{ borderBottom: '1px solid #f3f4f6' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <td style={{ padding: '0.7rem 1rem', color: '#dc2626', fontFamily: 'monospace', fontWeight: 600 }}>{r.ee_number}</td>
                    <td style={{ padding: '0.7rem 1rem', fontWeight: 600 }}>{r.last_name}</td>
                    <td style={{ padding: '0.7rem 1rem' }}>{r.first_name}</td>
                    <td style={{ padding: '0.7rem 1rem' }}>
                      {r.total_hours != null
                        ? <strong>{toHourMinuteString(toMinutes(r.total_hours) - toMinutes(r.overtime_hours || 0))}</strong>
                        : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.7rem 1rem' }}>
                      {toMinutes(r.overtime_hours) > 0
                        ? <span style={{ color: '#dc2626', fontWeight: 600 }}>{toHourMinuteString(toMinutes(r.overtime_hours))}</span>
                        : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.7rem 1rem' }}>
                      {r.total_hours != null
                        ? <strong style={{ color: toMinutes(r.overtime_hours) > 0 ? '#dc2626' : '#111827' }}>{toHourMinuteString(toMinutes(r.total_hours))}</strong>
                        : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.7rem 1rem', color: '#6b7280', fontSize: '0.82rem' }}>
                      {r.entry_count > 0 ? <span>{r.entry_count} entries</span> : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.7rem 1rem' }}><StatusBadge status={r.status || 'not_submitted'} /></td>
                    <td style={{ padding: '0.7rem 1rem' }}>
                      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'nowrap' }}>
                        {r.id && (
                          <button onClick={() => openDetail(r)} style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', color: '#374151', cursor: 'pointer' }}>VIEW</button>
                        )}
                        {r.status === 'submitted' && (<>
                          <button onClick={() => { setReviewTarget(r); setReviewAction('APPROVE'); setReviewNotes(''); }}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', fontWeight: 600, border: '1px solid #16a34a', borderRadius: '4px', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer' }}>✓</button>
                          <button onClick={() => { setReviewTarget(r); setReviewAction('REJECT'); setReviewNotes(''); }}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', fontWeight: 600, border: '1px solid #dc2626', borderRadius: '4px', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}>✗</button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Review Modal ── */}
      <Modal open={!!reviewTarget}
        title={`${reviewAction === 'APPROVE' ? '✅ Approve' : '❌ Reject'} Timesheet`}
        onClose={() => setReviewTarget(null)}
        footer={<>
          <Btn variant="secondary" onClick={() => setReviewTarget(null)}>CANCEL</Btn>
          <Btn variant={reviewAction === 'APPROVE' ? 'success' : 'danger'} onClick={handleReview} disabled={reviewing}>
            {reviewing ? 'SAVING…' : reviewAction}
          </Btn>
        </>}>
        {reviewTarget && (
          <div>
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
              {[
                ['Mechanic', `${reviewTarget.last_name}, ${reviewTarget.first_name}`],
                ['EE#',      reviewTarget.ee_number],
                ['Week',     reviewTarget.week_ending_date],
                ['Total Hours', `${toHourMinuteString(toMinutes(reviewTarget.total_hours || 0))} hrs`],
              ].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', fontSize: '0.875rem', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280' }}>{l}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
            <Field label="Reviewer Notes">
              <textarea className="textarea" value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} rows={3}
                placeholder={reviewAction === 'REJECT' ? 'Reason for rejection…' : 'Optional notes…'} />
            </Field>
          </div>
        )}
      </Modal>

      {/* ── Detail Modal ── */}
      <WideModal
        open={!!detailWeek}
        title={`Entries: ${detailWeek?.last_name}, ${detailWeek?.first_name} – ${detailWeek?.week_ending_date}`}
        onClose={() => { setDetailWeek(null); setEditingEntry(null); setWideModalNote(''); }}
        footer={
          detailWeek?.status === 'submitted' ? (
            <div style={{ width: '100%' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>
                Reviewer Notes
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                <textarea
                  value={wideModalNote}
                  onChange={e => setWideModalNote(e.target.value)}
                  placeholder="Add notes before approving or rejecting…"
                  style={{
                    flex: 1, boxSizing: 'border-box',
                    padding: '0.4rem 0.65rem', fontSize: '0.875rem',
                    border: '1px solid #d1d5db', borderRadius: '5px',
                    fontFamily: 'inherit', resize: 'none', outline: 'none',
                    color: '#111827', height: '42px',
                  }}
                  onFocus={e => e.target.style.borderColor = '#dc2626'}
                  onBlur={e => e.target.style.borderColor = '#d1d5db'}
                />
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                  <Btn variant="secondary" style={{ height: '42px' }}
                    onClick={() => { setDetailWeek(null); setEditingEntry(null); setWideModalNote(''); }}>
                    CLOSE
                  </Btn>
                  <Btn variant="danger" style={{ height: '42px' }} disabled={reviewing}
                    onClick={async () => {
                      setReviewing(true);
                      try {
                        await reviewTimesheet(detailWeek.id, 'REJECT', wideModalNote);
                        showToast('Timesheet rejected ✓', 'success');
                        setDetailWeek(null); setEditingEntry(null); setWideModalNote('');
                        fetchData();
                      } catch (err) {
                        showToast(err.message || 'Reject failed', 'error');
                      } finally { setReviewing(false); }
                    }}>
                    {reviewing ? 'SAVING…' : '✗ REJECT'}
                  </Btn>
                  <Btn variant="success" style={{ height: '42px' }} disabled={reviewing}
                    onClick={async () => {
                      setReviewing(true);
                      try {
                        await reviewTimesheet(detailWeek.id, 'APPROVE', wideModalNote);
                        showToast('Timesheet approved ✓', 'success');
                        setDetailWeek(null); setEditingEntry(null); setWideModalNote('');
                        fetchData();
                      } catch (err) {
                        showToast(err.message || 'Approve failed', 'error');
                      } finally { setReviewing(false); }
                    }}>
                    {reviewing ? 'SAVING…' : '✓ APPROVE'}
                  </Btn>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Btn variant="secondary" onClick={() => { setDetailWeek(null); setEditingEntry(null); setWideModalNote(''); }}>
                CLOSE
              </Btn>
            </div>
          )
        }
      >
        {loadingDetail ? <Spinner /> : (
          <>
            <style>{`
              .entry-card { border:1px solid #e5e7eb; border-radius:8px; padding:0.9rem; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,0.06); margin-bottom:0.75rem; }
              .entry-card.editing { background:#fff7f7; }
              .card-top-row { display:flex; justify-content:space-between; align-items:center; margin-bottom:0.5rem; }
              .card-date { font-family:monospace; font-size:1rem; font-weight:700; color:#111; }
              .card-badges { display:flex; gap:0.75rem; margin-bottom:0.4rem; flex-wrap:wrap; align-items:center; }
              .card-badges span { font-size:0.8rem; color:#9ca3af; }
              .card-badges strong.wo { color:#7c3aed; }
              .card-badges strong.job { color:#dc2626; }
              .card-desc { margin:0 0 0.5rem; font-size:0.92rem; color:#374151; line-height:1.45; }
              .card-times { display:flex; gap:1rem; align-items:center; flex-wrap:wrap; }
              .card-times .t-in  { font-size:0.88rem; color:#16a34a; font-family:monospace; font-weight:600; }
              .card-times .t-out { font-size:0.88rem; color:#dc2626; font-family:monospace; font-weight:600; }
              .card-times .t-hrs { font-size:0.88rem; font-weight:800; color:#111; }
              .card-by { margin:0.4rem 0 0; font-size:0.76rem; color:#9ca3af; }
              .edit-card-form { display:flex; flex-direction:column; gap:0.55rem; }
              .field-lbl { display:block; font-size:0.7rem; font-weight:700; color:#9ca3af; text-transform:uppercase; letter-spacing:0.04em; margin-bottom:0.2rem; }
              .edit-card-form input, .edit-card-form select {
                width:100%; box-sizing:border-box;
                font-size:0.95rem; padding:0.35rem 0.5rem;
                border:1px solid #d1d5db; border-radius:5px;
                font-family:inherit;
              }
              .edit-card-form input:focus, .edit-card-form select:focus { outline:none; border-color:#f59e0b; box-shadow:0 0 0 2px #fef3c7; }
              .edit-card-form input[readonly] { background:#f9fafb; color:#9ca3af; }
              .time-hrs-grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:0.5rem; }
              .card-edit-actions { display:flex; gap:0.5rem; margin-top:0.3rem; }
              .card-edit-actions button { flex:1; padding:0.5rem; font-size:0.9rem; border-radius:6px; cursor:pointer; font-weight:700; border:none; }
              .btn-save-card   { background:#16a34a; color:#fff; }
              .btn-cancel-card { background:#f3f4f6; color:#6b7280; }
              .btn-icon-edit {
                display:inline-flex; align-items:center; justify-content:center;
                width:34px; height:34px; border:none; border-radius:7px;
                background:transparent; color:#d97706; cursor:pointer;
                transition:background 0.15s, color 0.15s;
              }
              .btn-icon-edit:hover { background:#fef3c7; color:#b45309; }
              .status-locked { display:inline-flex; align-items:center; gap:0.3rem; font-size:0.75rem; color:#9ca3af; font-style:italic; }
              .entries-table { width:100%; border-collapse:collapse; table-layout:fixed; font-size:0.95rem; }
              .entries-table th {
                padding:0.6rem 0.5rem; text-align:left; font-size:0.8rem; font-weight:700;
                color:#6b7280; text-transform:uppercase; border-bottom:2px solid #e5e7eb;
                white-space:nowrap; overflow:hidden; text-overflow:ellipsis; background:#f9fafb;
              }
              .entries-table td {
                padding:0.55rem 0.5rem; border-bottom:1px solid #f3f4f6;
                overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
              }
              .entries-table tr:hover td { background:#fafafa; }
              .entries-table tr.row-editing td { background:#fff7f7; }
              .td-mono  { font-family:monospace; }
              .td-wo    { font-family:monospace; color:#7c3aed; }
              .td-job   { font-family:monospace; color:#dc2626; }
              .td-in    { font-family:monospace; color:#16a34a; font-weight:600; }
              .td-out   { font-family:monospace; color:#dc2626; font-weight:600; }
              .td-hrs   { font-weight:800; }
              .td-muted { color:#9ca3af; }
              .edit-inp    { width:100%; box-sizing:border-box; font-size:0.93rem; padding:0.25rem 0.4rem; border:1px solid #d1d5db; border-radius:4px; font-family:inherit; }
              .edit-inp-ro { background:#f9fafb; border-color:#e5e7eb; color:#9ca3af; }
              .tbl-btn-save   { padding:0.3rem 0.6rem; font-size:0.82rem; font-weight:700; border:1px solid #16a34a; border-radius:4px; background:#f0fdf4; color:#16a34a; cursor:pointer; }
              .tbl-btn-cancel { padding:0.3rem 0.6rem; font-size:0.82rem; font-weight:600; border:1px solid #d1d5db; border-radius:4px; background:#fff; color:#6b7280; cursor:pointer; }
            `}</style>

            {(() => {
              const canEditEntry = detailWeek?.status !== 'approved' && detailWeek?.status !== 'rejected';

              return isMobile ? (

                /* ══════════ MOBILE CARDS ══════════ */
                <div>
                  {detailEntries.length === 0
                    ? <p style={{ textAlign: 'center', color: '#9ca3af', padding: '1.5rem' }}>No entries.</p>
                    : detailEntries.map(en => {
                      const isEditing = editingEntry?.id === en.id;
                      return (
                        <div key={en.id} className={`entry-card${isEditing ? ' editing' : ''}`}>
                          {isEditing ? (
                            <div className="edit-card-form">
                              <div>
                                <label className="field-lbl">Date</label>
                                <DatePicker value={editForm.entry_date} onChange={val => setEditForm(f => ({ ...f, entry_date: val }))} small />
                              </div>
                              {[['WO #', 'work_order'], ['Job #', 'job_number']].map(([lbl, field]) => (
                                <div key={field}>
                                  <label className="field-lbl">{lbl}</label>
                                  <input value={editForm[field]} onChange={eUpd(field)} placeholder={lbl} />
                                </div>
                              ))}
                              {/* ── Location select — mobile edit ── */}
                              <div>
                                <label className="field-lbl">Loc</label>
                                <select value={editForm.location || 'Shop'} onChange={eUpd('location')}>
                                  <option value="Shop">Shop</option>
                                  <option value="Off-Site">Off-Site</option>
                                </select>
                              </div>
                              <div>
                                <label className="field-lbl">Description</label>
                                <input value={editForm.description} onChange={eUpd('description')} placeholder="Description" />
                              </div>
                              <div className="time-hrs-grid">
                                <div>
                                  <label className="field-lbl">Time In</label>
                                  <input type="time" value={editForm.time_in} onChange={eUpd('time_in')} />
                                </div>
                                <div>
                                  <label className="field-lbl">Time Out</label>
                                  <input type="time" value={editForm.time_out} onChange={eUpd('time_out')} />
                                </div>
                                <div>
                                  <label className="field-lbl">Hrs</label>
                                  <input value={editForm.hours_worked} readOnly />
                                </div>
                              </div>
                              <div className="card-edit-actions">
                                <button className="btn-save-card" onClick={handleSaveEdit} disabled={savingEdit}>
                                  {savingEdit ? '…' : '✓ SAVE'}
                                </button>
                                <button className="btn-cancel-card" onClick={() => setEditingEntry(null)}>✕ CANCEL</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="card-top-row">
                                <span className="card-date">{en.entry_date}</span>
                                {canEditEntry ? (
                                  <button className="btn-icon-edit" title="Edit" onClick={() => openEditEntry(en)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                  </button>
                                ) : (
                                  <span className="status-locked">🔒 locked</span>
                                )}
                              </div>
                              <div className="card-badges">
                                <span>WO# <strong className="wo">{en.work_order || '—'}</strong></span>
                                <span>Job# <strong className="job">{en.job_number}</strong></span>
                                {/* ── Location badge — mobile view ── */}
                                <LocationBadge location={en.location} />
                              </div>
                              <p className="card-desc">{en.description}</p>
                              <div className="card-times">
                                <span className="t-in">▶ {formatTime(en.time_in)}</span>
                                <span className="t-out">◀ {formatTime(en.time_out)}</span>
                                <span className="t-hrs">{toHourMinuteString(toMinutes(en.hours_worked))}h</span>
                              </div>
                              {en.entered_by && <p className="card-by">By: {en.entered_by}</p>}
                            </>
                          )}
                        </div>
                      );
                    })}
                </div>

              ) : (

                /* ══════════ DESKTOP TABLE ══════════ */
                <table className="entries-table">
                  <colgroup>
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '8%' }} />  {/* Location */}
                    <col style={{ width: '22%' }} /> {/* Description */}
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '13%' }} /> {/* Actions */}
                  </colgroup>
                  <thead>
                    <tr>
                      {['Date', 'WO#', 'Job#', 'Loc', 'Description', 'In', 'Out', 'Hrs', 'Entered By', 'Actions'].map(h => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detailEntries.length === 0
                      ? <tr><td colSpan={10} style={{ padding: '1.5rem', textAlign: 'center', color: '#9ca3af' }}>No entries.</td></tr>
                      : detailEntries.map(en => {
                        const isEditing = editingEntry?.id === en.id;
                        return (
                          <tr key={en.id} className={isEditing ? 'row-editing' : ''}>
                            {isEditing ? (
                              <>
                                {/* Date */}
                                <td><DatePicker value={editForm.entry_date} onChange={val => setEditForm(f => ({ ...f, entry_date: val }))} small /></td>
                                {/* WO# */}
                                <td><input className="edit-inp" value={editForm.work_order} onChange={eUpd('work_order')} /></td>
                                {/* Job# */}
                                <td><input className="edit-inp" value={editForm.job_number} onChange={eUpd('job_number')} /></td>
                                {/* Location — dropdown in edit row */}
                                <td>
                                  <select
                                    className="edit-inp"
                                    value={editForm.location || 'Shop'}
                                    onChange={eUpd('location')}
                                    style={{ fontFamily: 'inherit' }}
                                  >
                                    <option value="Shop">Shop</option>
                                    <option value="Off-Site">Off-Site</option>
                                  </select>
                                </td>
                                {/* Description */}
                                <td><input className="edit-inp" value={editForm.description} onChange={eUpd('description')} /></td>
                                {/* Time In */}
                                <td><input type="time" className="edit-inp" value={editForm.time_in} onChange={eUpd('time_in')} /></td>
                                {/* Time Out */}
                                <td><input type="time" className="edit-inp" value={editForm.time_out} onChange={eUpd('time_out')} /></td>
                                {/* Hours */}
                                <td><input className="edit-inp edit-inp-ro" value={editForm.hours_worked} readOnly /></td>
                                {/* Entered By */}
                                <td className="td-muted">{en.entered_by || '—'}</td>
                                {/* Actions */}
                                <td>
                                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                                    <button className="tbl-btn-save" onClick={handleSaveEdit} disabled={savingEdit}>
                                      {savingEdit ? '…' : '✓ SAVE'}
                                    </button>
                                    <button className="tbl-btn-cancel" onClick={() => setEditingEntry(null)}>✕</button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="td-mono">{en.entry_date}</td>
                                <td className="td-wo">{en.work_order || '—'}</td>
                                <td className="td-job">{en.job_number}</td>
                                {/* Location badge — desktop view */}
                                <td><LocationBadge location={en.location} /></td>
                                <td title={en.description}>{en.description}</td>
                                <td className="td-in">{formatTime(en.time_in)}</td>
                                <td className="td-out">{formatTime(en.time_out)}</td>
                                <td className="td-hrs">{toHourMinuteString(toMinutes(en.hours_worked))}h</td>
                                <td className="td-muted">{en.entered_by || '—'}</td>
                                <td>
                                  {canEditEntry ? (
                                    <button className="btn-icon-edit" title="Edit Entry" onClick={() => openEditEntry(en)}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                      </svg>
                                    </button>
                                  ) : (
                                    <span className="status-locked">🔒 locked</span>
                                  )}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              );
            })()}
          </>
        )}
      </WideModal>

      {/* ── Manual Entry Modal ── */}
      <Modal open={manualOpen} title="Add Manual Entry" onClose={closeManualModal}
        footer={<>
          <Btn variant="secondary" onClick={closeManualModal}>CANCEL</Btn>
          <Btn onClick={handleManualEntry} disabled={savingManual}>{savingManual ? 'SAVING…' : 'ADD ENTRY'}</Btn>
        </>}>
        <form onSubmit={handleManualEntry}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Field label="Mechanic" required>
              <Select value={manualForm.employee_id} onChange={mUpd('employee_id')} required>
                <option value="">— Select Mechanic —</option>
                {mechanics.map(m => (
                  <option key={m.id} value={m.id}>{m.ee_number} – {m.last_name}, {m.first_name}</option>
                ))}
              </Select>
            </Field>
            <Field label="Date" required>
              <DatePicker
                value={manualForm.entry_date}
                onChange={val => setManualForm(f => ({ ...f, entry_date: val }))}
              />
            </Field>
            <Field label="WO #">
              <Input type="text" value={manualForm.work_order} onChange={mUpd('work_order')} placeholder="WO-1021" />
            </Field>
            <Field label="Unit / Job Number" required>
              <Input type="text" value={manualForm.job_number} onChange={mUpd('job_number')} placeholder="JOB-2841" required />
            </Field>
            {/* ── Location dropdown — manual entry modal ── */}
            <Field label="Loc" required>
              <LocationSelect
                value={manualForm.location}
                onChange={mUpd('location')}
              />
            </Field>
            <Field label="Time In" required>
              <Input type="time" value={manualForm.time_in} onChange={mUpd('time_in')} required />
            </Field>
            <Field label="Time Out" required>
              <Input type="time" value={manualForm.time_out} onChange={mUpd('time_out')} required />
            </Field>
            <Field label={`Hours${manualForm.hours_worked ? ` (${manualForm.hours_worked})` : ''}`}>
              <Input type="number" value={manualForm.hours_worked} readOnly placeholder="Auto" />
            </Field>
            <Field label="Description" required>
              <Input type="text" value={manualForm.description} onChange={mUpd('description')} placeholder="Work description…" required />
            </Field>
          </div>
        </form>
      </Modal>

      <Toast message={toast.message} type={toast.type} onClose={clearToast} />
    </div>
  );
}